import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Phone, Loader2, Mic, ShieldCheck, Play, AlertTriangle } from "lucide-react";

// Advisor-assisted calling pilot (Helm). Truthful states: disabled / unconfigured / simulation.
// Initiating only calls the ADVISOR first; the consumer is dialed after explicit accept (server-side).
// Recording (when enabled) requires advisor consent here + consumer DTMF consent in-call before any bridge.
const REC_STATUS_LABEL = {
  advisor_consent_recorded: "Awaiting call", consumer_consent_pending: "Awaiting consumer consent",
  record_requested: "Starting recording", record_active: "Recording", saved: "Saved",
  saved_simulated: "Saved (simulated)", save_failed: "Save failed",
  record_start_failed: "Recording failed to start", consumer_declined: "Consumer declined",
  consumer_timeout: "No consumer response",
};

export const CallPilot = ({ householdId }) => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState(null);
  const [record, setRecord] = useState(false);
  const [recConsent, setRecConsent] = useState(false);
  const [recs, setRecs] = useState([]);
  const [playingUrl, setPlayingUrl] = useState(null);

  const loadStatus = async () => {
    try { const { data } = await api.get("/telephony/status", { params: { household_id: householdId } }); setSt(data); }
    catch { setSt({ enabled: false, configured: false, simulation: true, has_mapping: false }); }
  };
  const loadRecs = async () => {
    try { const { data } = await api.get(`/telephony/recordings/household/${householdId}`); setRecs(data.recordings || []); }
    catch { setRecs([]); }
  };
  useEffect(() => { loadStatus(); loadRecs(); /* eslint-disable-next-line */ }, [householdId]);

  const startCall = async () => {
    setBusy(true); setMsg(null);
    try {
      const { data } = await api.post("/telephony/callback", {
        household_id: householdId, record, recording_consent: record ? recConsent : false,
      });
      if (data && data.ok === false) {
        setMsg({ ok: false, text: data.message || "Could not start the call." });
      } else {
        setMsg({ ok: true, text: data.simulated
          ? (record ? "Simulated: your phone rings first; the consumer must press 1 to consent to recording before connecting."
                    : "Simulated: your phone would ring first; press 1 to connect the consumer.")
          : (record ? "Calling your phone now — answer, then the consumer must consent to recording before you connect."
                    : "Calling your phone now — answer, then press 1 to connect the consumer.") });
      }
      loadRecs();
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Could not start the call." });
    } finally { setBusy(false); }
  };

  const play = async (r) => {
    try {
      const res = await api.get(`/telephony/recordings/${r.id}/audio`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      setPlayingUrl(url);
    } catch (e) {
      setMsg({ ok: false, text: e?.response?.data?.detail || "Recording unavailable." });
    }
  };

  if (!st) return null;
  const blockReason = !st.enabled ? "Calling is switched off for this environment, so no call can be placed."
    : !st.has_mapping ? "No SBA business number is mapped to your account yet."
    : !st.has_answering_phone ? "No answering phone is set for your business number."
    : st.household_reason || null;
  const blocked = !!blockReason;
  const reason = blockReason || (st.simulation ? "Pilot (simulation): no live call is placed." : null);

  return (
    <div className="rounded-lg border border-[#E2E8F0] p-4 bg-white" data-testid="callpilot">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[#0B192C] font-semibold text-sm">
          <Phone className="w-4 h-4 text-[#1E3E62]" /> Advisor-assisted call{st.simulation ? " (pilot)" : ""}
        </div>
        <Button size="sm" disabled={busy || blocked || (record && !recConsent)} onClick={startCall}
          data-testid="callpilot-start"
          className="bg-[#1E3E62] hover:bg-[#162f4d] text-white text-xs rounded-full px-4 disabled:opacity-50">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : "Call — agent first"}
        </Button>
      </div>
      <p className="mt-2 text-xs text-[#475569]" data-testid="callpilot-explainer">
        Rings your answering phone first. When you answer and press 1, the beneficiary is dialed and connected from your SBA business number.
      </p>

      {st.recording_enabled && !blocked && (
        <div className="mt-3 rounded-md bg-[#F8FAFC] border border-[#E2E8F0] p-3 space-y-2" data-testid="callpilot-recording-controls">
          <label className="flex items-center gap-2 text-xs text-[#0B192C] font-medium cursor-pointer">
            <input type="checkbox" checked={record} onChange={(e) => { setRecord(e.target.checked); if (!e.target.checked) setRecConsent(false); }}
              data-testid="callpilot-record-toggle" />
            <Mic className="w-3.5 h-3.5 text-[#1E3E62]" /> Record this call
          </label>
          {record && (
            <label className="flex items-start gap-2 text-[11px] text-[#475569] cursor-pointer pl-1" data-testid="callpilot-record-consent-label">
              <input type="checkbox" checked={recConsent} onChange={(e) => setRecConsent(e.target.checked)} className="mt-0.5"
                data-testid="callpilot-record-consent" />
              <span><ShieldCheck className="w-3 h-3 inline mr-1 text-[#047857]" />
                I confirm recording consent. The consumer will also hear a disclosure and must press 1 to consent before any recording or connection (disclosure {st.recording_disclosure_version}).</span>
            </label>
          )}
        </div>
      )}

      {reason && <p className="mt-2 text-xs text-[#64748B]" data-testid="callpilot-reason">{reason}</p>}
      {msg && (
        <p className={`mt-2 text-xs ${msg.ok ? "text-[#047857]" : "text-[#B91C1C]"}`} data-testid="callpilot-result">
          {msg.text}
        </p>
      )}

      {recs.length > 0 && (
        <div className="mt-4 border-t border-[#EEF2F6] pt-3" data-testid="callpilot-recordings">
          <p className="text-xs font-semibold text-[#0B192C] mb-2 flex items-center gap-1.5"><Mic className="w-3.5 h-3.5" /> Call recordings</p>
          <ul className="space-y-2">
            {recs.map((r) => (
              <li key={r.id} className="text-xs flex items-center justify-between gap-2 rounded-md border border-[#EEF2F6] px-2.5 py-1.5" data-testid={`recording-${r.id}`}>
                <span className="flex flex-col">
                  <span className="text-[#0B192C] font-medium">{REC_STATUS_LABEL[r.status] || r.status}</span>
                  <span className="text-[#94A3B8]">
                    consent: advisor {r.advisor_consent ? "✓" : "—"} · consumer {r.consumer_consent ? "✓" : "—"}
                    {r.retention_review_required ? " · retention review" : ""}{r.legal_hold ? " · legal hold" : ""}
                  </span>
                  {r.failure && <span className="text-[#B91C1C] flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> recording unavailable</span>}
                </span>
                {r.has_audio ? (
                  <Button size="sm" variant="outline" onClick={() => play(r)} data-testid={`recording-play-${r.id}`}
                    className="text-[11px] h-7 px-2 rounded-full"><Play className="w-3 h-3 mr-1" /> Play</Button>
                ) : (
                  <span className="text-[#94A3B8] text-[11px]" data-testid={`recording-noaudio-${r.id}`}>no audio</span>
                )}
              </li>
            ))}
          </ul>
          {playingUrl && (
            <audio controls autoPlay src={playingUrl} className="mt-2 w-full h-8" data-testid="recording-audio-player" />
          )}
        </div>
      )}
    </div>
  );
};
